import { Controller } from "@hotwired/stimulus"

/**
 * Inbox Filters drawer cascade (CONEK-8035).
 *
 * Category and Service follow the selected ticket types, Subcategory follows
 * the selected categories. Each source multi-select sits in an element marked
 * `data-ticket-filter-reloads="<frame id>"`; when its selection changes, the
 * named turbo-frame reloads from `urlValue` (TicketsController#filter_fields)
 * with the drawer's current choices, and the server re-renders the dependent
 * lists — dropping any selected value they no longer offer.
 *
 * Ui::MultiSelectComponent keeps its selection in hidden inputs inside its
 * `values` target and does not fire `change` for Select all / Clear, so the
 * controller watches those containers with one MutationObserver on the drawer
 * fields instead of listening for events. A reloaded frame brings new
 * containers; the subtree observer sees them without re-binding.
 *
 * Mounted on the drawer's form. Each multi-select also submits an empty
 * `key[]` sentinel; `dropBlanks` removes empty values from the form data so
 * Apply advances to a clean URL (and Save view never sees blank keys).
 *
 * A reload answers the choices made when it was requested. If any drawer
 * field changed while it was in flight (e.g. a subcategory picked right after
 * its category), rendering it would wipe that pick, so `turbo:before-frame-render`
 * skips the stale render and asks again with the current choices.
 */
export default class extends Controller {
  static values = { url: String, delay: { type: Number, default: 250 } }

  connect() {
    this._timers = new Map()
    this._requested = new Map()
    this._observer = new MutationObserver((mutations) =>
      this._onMutations(mutations),
    )
    this._observer.observe(this.element, { childList: true, subtree: true })
    this._onBeforeFrameRender = this._onBeforeFrameRender.bind(this)
    this.element.addEventListener(
      "turbo:before-frame-render",
      this._onBeforeFrameRender,
    )
  }

  disconnect() {
    this._observer?.disconnect()
    this._timers.forEach((timer) => clearTimeout(timer))
    this._timers.clear()
    this._requested.clear()
    this.element.removeEventListener(
      "turbo:before-frame-render",
      this._onBeforeFrameRender,
    )
  }

  _onMutations(mutations) {
    const frames = new Set()
    mutations.forEach((mutation) => {
      const target = mutation.target
      if (!(target instanceof Element)) return
      if (target.getAttribute("data-ui--multi-select-target") !== "values")
        return
      const source = target.closest("[data-ticket-filter-reloads]")
      if (source) frames.add(source.dataset.ticketFilterReloads)
    })
    frames.forEach((frameId) => this._schedule(frameId))
  }

  _schedule(frameId) {
    clearTimeout(this._timers.get(frameId))
    this._timers.set(
      frameId,
      setTimeout(() => {
        this._timers.delete(frameId)
        this.reload(frameId)
      }, this.delayValue),
    )
  }

  reload(frameId) {
    const frame = document.getElementById(frameId)
    if (!frame) return
    const url = this._urlFor()
    this._requested.set(frameId, url)
    frame.src = url
  }

  _onBeforeFrameRender(event) {
    const frameId = event.target?.id
    const requested = this._requested.get(frameId)
    if (requested === undefined) return

    if (this._urlFor() === requested) {
      this._requested.delete(frameId)
      return
    }
    // Keep the current fields: the stale answer must not replace them.
    event.detail.render = () => undefined
    this.reload(frameId)
  }

  // `formdata` fires whenever the form is serialized (Turbo's submission too).
  dropBlanks(event) {
    const data = event.formData
    const kept = [...data.entries()].filter(
      ([, value]) => typeof value !== "string" || value !== "",
    )
    ;[...new Set([...data.keys()])].forEach((key) => data.delete(key))
    kept.forEach(([key, value]) => data.append(key, value))
  }

  // The drawer's form fields as a query on the fields endpoint. Only the
  // drawer's own choices matter to it, but sending the whole form is harmless.
  _urlFor() {
    const form = this.element.closest("form")
    const params = new URLSearchParams()
    if (form) {
      new FormData(form).forEach((value, key) => {
        if (typeof value === "string" && value !== "") params.append(key, value)
      })
    }
    const query = params.toString()
    return query ? `${this.urlValue}?${query}` : this.urlValue
  }
}
